import { useEffect, useRef } from 'react'

/**
 * Custom hook for Modal side effects
 * Handles body scroll lock, ESC key listener, and focus trap
 *
 * @param {Object} params - Hook parameters
 * @param {boolean} params.isOpen - Modal open state
 * @param {Function} params.onClose - Close callback
 * @param {boolean} params.closeOnEscape - Enable ESC key to close
 * @param {boolean} params.preventBodyScroll - Lock body scroll when open
 */
const useModalEffects = ({ isOpen, onClose, closeOnEscape, preventBodyScroll }) => {
  const previousActiveElement = useRef(null)

  // Body scroll lock
  useEffect(() => {
    if (!isOpen || !preventBodyScroll) return

    // Save scrollbar width before locking
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

    // Lock scroll
    document.body.style.overflow = 'hidden'

    // Prevent layout shift from scrollbar disappearing
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }

    return () => {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    }
  }, [isOpen, preventBodyScroll])

  // ESC key listener
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, closeOnEscape, onClose])

  // Focus trap and restoration
  useEffect(() => {
    if (!isOpen) return

    // Save currently focused element
    previousActiveElement.current = document.activeElement

    // Focus first focusable element in modal
    setTimeout(() => {
      const modal = document.querySelector('[role="dialog"]')
      if (modal) {
        const focusableElements = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        if (focusableElements.length > 0) {
          focusableElements[0].focus()
        }
      }
    }, 100)

    // Return focus on close
    return () => {
      if (previousActiveElement.current) {
        previousActiveElement.current.focus()
      }
    }
  }, [isOpen])
}

export default useModalEffects
